import React, { Suspense, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { heroData } from '../data/mock';
import { ArrowRight, Sparkles, Play } from 'lucide-react';

const Spline = React.lazy(() => import('@splinetool/react-spline'));

const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.9]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 10]);

  const scrollToPricing = () => {
    document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    window.location.href = 'mailto:arnabsarkarprofessional@gmail.com?subject=Free Strategy Call Request&body=Hi Arnab,%0D%0A%0D%0AI would like to book a free strategy call to discuss my web design project.%0D%0A%0D%0APlease let me know your availability.%0D%0A%0D%0AThank you!';
  };

  return (
    <section
      ref={containerRef}
      id="hero"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
    >
      {/* Parallax Background Elements */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 150]) }}
      >
        {/* Large gradient orbs */}
        <div className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-violet-600/30 via-purple-600/20 to-transparent blur-[100px] animate-pulse-glow" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-bl from-blue-600/25 via-indigo-600/15 to-transparent blur-[100px] animate-pulse-glow" style={{ animationDelay: '2s' }} />
        <div className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-purple-600/20 via-violet-600/10 to-transparent blur-[80px] animate-pulse-glow" style={{ animationDelay: '4s' }} />
      </motion.div>

      {/* Floating 3D geometric shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div 
          className="absolute top-20 left-[15%] w-20 h-20 border border-violet-500/30 rotate-45 animate-float-slow"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, 200]) }}
        />
        <motion.div 
          className="absolute top-40 right-[20%] w-16 h-16 border border-blue-500/30 rounded-full animate-float"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, 150]) }}
        />
        <motion.div 
          className="absolute bottom-40 left-[10%] w-12 h-12 bg-gradient-to-br from-violet-500/20 to-blue-500/10 rounded-lg animate-rotate-slow"
          style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
        />
        <motion.div 
          className="absolute top-60 left-[5%] w-8 h-8 bg-violet-500/30 rounded-full blur-sm animate-float"
          style={{ animationDelay: '1s' }}
        />
        <motion.div 
          className="absolute bottom-60 right-[15%] w-24 h-24 border-2 border-blue-500/20 rounded-2xl rotate-12 animate-float-slow"
          style={{ animationDelay: '2s', y: useTransform(scrollYProgress, [0, 1], [0, -150]) }}
        />
      </div>

      {/* Grid Pattern with Parallax */}
      <motion.div 
        className="absolute inset-0 opacity-[0.03]"
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(139, 92, 246, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px'
          }}
        />
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32"
        style={{ opacity, scale }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="text-left">
            {/* Premium Name Display */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-violet-400/70 text-sm tracking-[0.3em] uppercase mb-3">Web Design Studio</p>
              <div className="relative inline-block">
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight">
                  <span className="text-white">Arnab</span>
                  <span className="text-gradient-violet ml-3">Sarkar</span>
                </h2>
                <div className="absolute -bottom-3 left-0 w-full h-[2px] bg-gradient-to-r from-violet-500 via-blue-500 to-transparent" />
                <div className="absolute -bottom-3 left-0 w-2/3 h-[3px] bg-gradient-to-r from-violet-500 to-blue-500 blur-sm" />
              </div>
            </motion.div>

            {/* Glass Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 glass px-5 py-2.5 mb-8 rounded-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Sparkles size={16} className="text-violet-400" />
              <span className="text-white/80 text-sm font-medium">Professional Web Design Services</span>
            </motion.div>

            {/* Headline */}
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Crafting Digital<br />
              Experiences That<br />
              <span className="relative inline-block">
                <span className="text-gradient-mixed">Convert.</span>
                <motion.span 
                  className="absolute -inset-4 bg-gradient-to-r from-violet-500/20 via-blue-500/20 to-transparent blur-2xl -z-10"
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              className="text-lg md:text-xl text-white/50 max-w-xl mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              From static showcases to full-scale enterprise solutions. Building digital experiences that drive real business results.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row items-start gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <button
                onClick={scrollToPricing}
                className="group relative bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 text-white px-8 py-4 text-lg font-medium flex items-center gap-3 overflow-hidden transition-all duration-500 hover:shadow-[0_0_50px_rgba(139,92,246,0.5)] rounded-xl animate-border-glow"
              >
                <span className="relative z-10">{heroData.ctaText}</span>
                <ArrowRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-violet-500 via-purple-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </button>
              <button
                onClick={handleBookCall}
                className="group glass text-white px-8 py-4 text-lg font-medium flex items-center gap-3 hover:bg-violet-500/10 transition-all duration-400 rounded-xl hover:border-violet-500/30"
              >
                <Play size={18} className="text-violet-400" />
                Get Custom Quote
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div 
              className="mt-16 grid grid-cols-3 gap-6 max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              {[
                { value: "50+", label: "Projects" },
                { value: "100%", label: "Satisfaction" },
                { value: "4 Day", label: "Delivery" }
              ].map((stat, index) => (
                <div key={index} className={`text-left group ${index > 0 ? 'border-l border-white/10 pl-6' : ''}`}>
                  <div className="text-2xl md:text-3xl font-bold text-gradient-violet group-hover:scale-110 transition-transform inline-block">{stat.value}</div>
                  <div className="text-white/40 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Side - 3D Spline Animation */}
          <motion.div 
            className="hidden lg:flex items-center justify-center relative"
            initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            style={{ rotateY: rotate }}
          >
            <div className="w-[700px] h-[700px] overflow-visible relative animate-float">
              {/* Glow behind 3D element */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/30 via-blue-500/20 to-transparent rounded-full blur-[120px] scale-75" />
              <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-32 h-32 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
                </div>
              }>
                <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
              </Suspense>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <motion.div 
          className="w-7 h-11 border-2 border-violet-500/40 rounded-full flex items-start justify-center p-2 glass"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-1.5 h-2.5 bg-gradient-to-b from-violet-500 to-blue-500 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
